import React, { useMemo, useState } from 'react';

// ── Inline media embeds for bot messages ─────────────────────────────────────
// When a Q&A answer (or any bot text) references visual sources — a YouTube
// video, an image URL, a direct video file — the raw link stays in the text
// untouched, and this component additionally renders the media inline under
// the message so the user doesn't have to leave the app.
//
// Only URL shapes we can classify with certainty are embedded (YouTube/Vimeo
// hosts, direct image/video file extensions); everything else — including the
// web-grounding redirect URLs in answer_sources — is ignored. Capped at 3
// embeds per message; broken images/videos remove themselves via onError.

interface MediaEmbedsProps {
  /** Raw message text (pre-markdown) — URLs are extracted from it. */
  text?: string | null;
  /** Optional answer_sources list; entries with a `url` are also considered.
   *  `resolved_url` (the real target behind a Vertex grounding redirect,
   *  resolved server-side) is preferred over the opaque `url`. */
  sources?: Array<{ url?: string; resolved_url?: string; title?: string }> | null;
}

type Embed =
  | { kind: 'youtube'; id: string; url: string }
  | { kind: 'vimeo'; id: string; url: string }
  | { kind: 'image'; url: string }
  | { kind: 'video'; url: string };

const URL_RE = /https?:\/\/[^\s<>"')\]]+/g;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif)$/i;
const VIDEO_EXT = /\.(mp4|webm|mov)$/i;

// Hosts serving logos/favicons/UI chrome, not content — a model answer
// sometimes inlines a YouTube logo or a favicon; embedding those as
// standalone "images" is noise.
const LOGO_HOSTS = /(^|\.)(?:yt3\.ggpht\.com|ggpht\.com|gstatic\.com|googleusercontent\.com)$/i;
const FAVICON_PATH = /favicon|\/s2\/favicons|logo/i;

function youTubeId(u: URL): string | null {
  const host = u.hostname.replace(/^(www|m)\./, '').toLowerCase();
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1).split('/')[0];
    return /^[\w-]{6,}$/.test(id) ? id : null;
  }
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (u.pathname === '/watch') {
      const id = u.searchParams.get('v') || '';
      return /^[\w-]{6,}$/.test(id) ? id : null;
    }
    const m = u.pathname.match(/^\/(?:shorts|embed|live)\/([\w-]{6,})/);
    if (m) return m[1];
  }
  return null;
}

// A YouTube THUMBNAIL url (i.ytimg.com/vi/{id}/hqdefault.jpg…) identifies the
// video — embed the actual player instead of a static squished image.
function youTubeThumbId(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, '').toLowerCase();
  if (host === 'i.ytimg.com' || host === 'i9.ytimg.com' || host === 'img.youtube.com') {
    const m = u.pathname.match(/^\/vi(?:_webp)?\/([\w-]{6,})\//);
    if (m) return m[1];
  }
  return null;
}

function vimeoId(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, '').toLowerCase();
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') return null;
  const m = u.pathname.match(/\/(\d{6,})(?:$|[/?])/);
  return m ? m[1] : null;
}

function classify(raw: string): Embed | null {
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const yt = youTubeId(u);
  if (yt) return { kind: 'youtube', id: yt, url: raw };
  // Before the generic image check: a YouTube thumbnail .jpg upgrades to the
  // actual video embed (dedupes with a direct link to the same video by id).
  const ytThumb = youTubeThumbId(u);
  if (ytThumb) return { kind: 'youtube', id: ytThumb, url: raw };
  const vm = vimeoId(u);
  if (vm) return { kind: 'vimeo', id: vm, url: raw };
  if (IMAGE_EXT.test(u.pathname)) {
    if (LOGO_HOSTS.test(u.hostname) || FAVICON_PATH.test(u.pathname)) return null;
    return { kind: 'image', url: raw };
  }
  if (VIDEO_EXT.test(u.pathname)) return { kind: 'video', url: raw };
  return null;
}

function extractEmbeds(
  text: string,
  sources: Array<{ url?: string; resolved_url?: string }>,
): Embed[] {
  const candidates: string[] = [];
  // exec loop instead of matchAll: the build targets ES5, where iterating a
  // RegExp string iterator fails to compile (TS2802).
  const urlRe = new RegExp(URL_RE.source, 'g');
  let match: RegExpExecArray | null;
  while ((match = urlRe.exec(text)) !== null) {
    // Trim trailing punctuation a sentence may glue onto the URL.
    candidates.push(match[0].replace(/[.,;:!?]+$/, ''));
  }
  for (const s of sources) {
    // Grounding citations are opaque redirect URLs; the backend resolves the
    // real target into resolved_url — that's the one that can classify.
    const url = (s && (s.resolved_url || s.url)) || '';
    if (typeof url === 'string' && url) candidates.push(url);
  }
  const embeds: Embed[] = [];
  const seen = new Set<string>();
  for (const raw of candidates) {
    const embed = classify(raw);
    if (!embed) continue;
    // Dedupe videos by platform id (same video linked twice in different
    // URL shapes), images/files by URL.
    const key = 'id' in embed ? `${embed.kind}:${embed.id}` : embed.url;
    if (seen.has(key)) continue;
    seen.add(key);
    embeds.push(embed);
    if (embeds.length >= 3) break;
  }
  return embeds;
}

const frameStyle: React.CSSProperties = {
  width: '100%',
  aspectRatio: '16 / 9',
  border: 0,
  borderRadius: 12,
  backgroundColor: '#000',
  display: 'block',
};

// ── YouTube embed with a graceful fallback ────────────────────────────────────
// Some videos disable embedding (uploader setting): the iframe then shows a
// dead "Video unavailable" box. The widget API posts an onError message once
// the "listening" handshake is done — on the embed-blocked codes we swap the
// frame for a clickable thumbnail card that opens the video on YouTube.

/** Parse a YouTube widget postMessage; returns the onError code or null. */
export function parseYouTubeErrorCode(data: unknown): number | null {
  try {
    const obj: any = typeof data === 'string' ? JSON.parse(data) : data;
    if (obj && typeof obj === 'object' && obj.event === 'onError') {
      const code = Number(obj.info);
      return Number.isFinite(code) ? code : null;
    }
  } catch {
    /* not a widget message */
  }
  return null;
}

// 2/5/100 = invalid or removed video / player failure → the video does NOT
// work: render nothing at all (product rule: show YouTube only when it works).
export const YT_DEAD_CODES = new Set([2, 5, 100]);
// 101/150 = the video EXISTS but forbids inline embedding → a clickable
// thumbnail card still gives the user a working path.
export const YT_BLOCKED_CODES = new Set([101, 150]);

const YouTubeThumbCard: React.FC<{ id: string }> = ({ id }) => {
  const [thumbBroken, setThumbBroken] = useState(false);
  const watchUrl = `https://www.youtube.com/watch?v=${id}`;
  if (thumbBroken) {
    return (
      <a
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 13, color: '#4a48b8', textDecoration: 'underline' }}
      >
        Watch on YouTube ↗
      </a>
    );
  }
  return (
    <a
      href={watchUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ position: 'relative', display: 'block', borderRadius: 12, overflow: 'hidden' }}
      title="Watch on YouTube"
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
        alt="YouTube video"
        loading="lazy"
        onError={() => setThumbBroken(true)}
        onLoad={(e) => {
          // YouTube serves a tiny gray placeholder (120×90) with HTTP 200 for
          // missing thumbnails — a gray card is worse than a plain link.
          if (e.currentTarget.naturalWidth <= 120) setThumbBroken(true);
        }}
        style={{ width: '100%', display: 'block' }}
      />
      <span
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 52,
          height: 36,
          borderRadius: 10,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            width: 0,
            height: 0,
            borderTop: '9px solid transparent',
            borderBottom: '9px solid transparent',
            borderLeft: '15px solid #fff',
            marginLeft: 3,
          }}
        />
      </span>
      <span
        style={{
          position: 'absolute',
          left: 10,
          bottom: 8,
          padding: '3px 8px',
          borderRadius: 8,
          backgroundColor: 'rgba(0,0,0,0.65)',
          color: '#fff',
          fontSize: 11.5,
          fontWeight: 500,
        }}
      >
        Watch on YouTube ↗
      </span>
    </a>
  );
};

const YouTubeEmbed: React.FC<{ id: string }> = ({ id }) => {
  const [status, setStatus] = useState<'ok' | 'blocked' | 'dead'>('ok');
  const frameRef = React.useRef<HTMLIFrameElement>(null);

  React.useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const code = parseYouTubeErrorCode(e.data);
      if (code === null) return;
      if (YT_DEAD_CODES.has(code)) setStatus('dead');
      else if (YT_BLOCKED_CODES.has(code)) setStatus('blocked');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (status === 'dead') return null; // a video that doesn't work shows nothing
  if (status === 'blocked') return <YouTubeThumbCard id={id} />;
  return (
    <iframe
      ref={frameRef}
      src={`https://www.youtube-nocookie.com/embed/${id}?enablejsapi=1`}
      title="YouTube video"
      loading="lazy"
      allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
      allowFullScreen
      style={frameStyle}
      onLoad={() => {
        // Widget-API handshake: without "listening" the player never posts
        // events (including the onError we need for the fallback).
        try {
          frameRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'listening', id, channel: 'widget' }),
            '*',
          );
        } catch {
          /* cross-origin quirk — the embed still works, only detection is off */
        }
      }}
    />
  );
};

/** An image that removes itself when the URL turns out not to load. */
const SafeImage: React.FC<{ url: string }> = ({ url }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
      <img
        src={url}
        alt=""
        loading="lazy"
        onError={() => setBroken(true)}
        style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 12, display: 'block' }}
      />
    </a>
  );
};

const SafeVideo: React.FC<{ url: string }> = ({ url }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return (
    <video
      src={url}
      controls
      preload="metadata"
      onError={() => setBroken(true)}
      style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 12, display: 'block' }}
    />
  );
};

// Inline media tags the model sometimes writes into an answer (<img> YouTube
// logos/thumbnails, stray <iframe>/<video>) render as tiny floating junk
// inside the prose. The DISPLAYED text strips them via this helper —
// MediaEmbeds extracts URLs from the ORIGINAL text, so embeddable media still
// renders, properly, below the message.
export function stripInlineMediaTags(text: string): string {
  return text
    .replace(/<(?:img|source|embed)\b[^>]*\/?>/gi, '')
    .replace(/<(iframe|video|object)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<(?:iframe|video|object)\b[^>]*\/?>/gi, '');
}

// A line that is NOTHING BUT an embeddable URL (the QA prompt asks for the
// video link on its own final line) reads as dead unclickable text once the
// player renders below it — drop it from the DISPLAYED text. URLs inside
// sentences and non-embeddable links stay untouched.
export function stripEmbeddableUrlLines(text: string): string {
  return text
    .split('\n')
    .filter((line) => {
      const candidate = line.trim().replace(/[.,;:!?]+$/, '');
      if (!candidate || /\s/.test(candidate)) return true; // not a lone URL line
      return classify(candidate) === null; // keep unless we embed it below
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const MediaEmbeds: React.FC<MediaEmbedsProps> = ({ text, sources }) => {
  const embeds = useMemo(
    () => extractEmbeds(String(text || ''), Array.isArray(sources) ? sources : []),
    [text, sources],
  );
  if (!embeds.length) return null;
  return (
    // width 100% keeps the embeds full-bubble-width even if a parent turns
    // out to be a flex row (the .section-label flex squeezed them once).
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, width: '100%' }}>
      {embeds.map((e) => {
        if (e.kind === 'youtube') {
          return <YouTubeEmbed key={`yt:${e.id}`} id={e.id} />;
        }
        if (e.kind === 'vimeo') {
          return (
            <iframe
              key={`vm:${e.id}`}
              src={`https://player.vimeo.com/video/${e.id}`}
              title="Vimeo video"
              loading="lazy"
              allow="encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              style={frameStyle}
            />
          );
        }
        if (e.kind === 'image') return <SafeImage key={e.url} url={e.url} />;
        return <SafeVideo key={e.url} url={e.url} />;
      })}
    </div>
  );
};

export default MediaEmbeds;
export { extractEmbeds };
